import React, { useState, useCallback, useMemo } from 'react';
import { useHotkeys } from '@mantine/hooks';
import { Tooltip, Loader } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { pickLocalFiles } from '@shakkerai/util-file';
import { config } from '@shakkerai/config';
import { getCanvasTools } from '@starflow/app/extension/elements';
import { notifyError, notifySuccess } from '@shakkerai/pro-components';
import { ACCEPTED_FILE_EXTS, insertLocalImage } from '@shakkerai/pro-utils';
import { GuidePopover, useGuidanceHooks } from '@shakkerai/pro-guidance';
import { viewportActions, setTempState, useTempState } from '@starflow/app/store';
import { mc } from '@shakkerai/report';
import { NavButton } from '.';
import { StarIconPopOver } from '@shakkerai/pro-components/src/iconPopOver';

// import iconSelect from './assets/select.svg';
// import iconSelectActive from './assets/select_active.svg';
// import iconMove from './assets/hand_1.svg';
// import iconMoveActive from './assets/hand_active.svg';

// const isOversea = config.env === 'aws';

const CanvasToolSelectList = [
  {
    name: 'select',
    icon: 'shk-shubiao',
    // activeIcon: iconSelectActive,
    tooltip: translate('tool_move'),
    hotkey: 'V',
  },
  {
    name: 'move',
    icon: 'icon-prodrag',
    // activeIcon: iconMoveActive,
    tooltip: translate('tool_hand'),
    hotkey: 'H',
  },
];

export const CanvasTools = React.memo(() => {
  const extensionTools = getCanvasTools();
  const currentCanvasTool = useTempState('currentCanvasTool');
  const [imageUploading, setImageUploading] = useState(false);
  const { visible: guidance9Visible, nextGuidance } = useGuidanceHooks(['Import']);

  const handleClickTool = useCallback((event) => {
    setTempState({ currentCanvasTool: event.currentTarget.name });
  }, []);

  const handleActiveTool = useCallback((tool) => {
    setTempState({ currentCanvasTool: tool.name });
    tool.onActive && tool.onActive();
  }, []);

  const handlePickImage = useCallback(async () => {
    try {
      const pickedFiles = await pickLocalFiles(ACCEPTED_FILE_EXTS, guidance9Visible ? 1 : Infinity);

      setImageUploading(true);

      let uploadedCount = 0;
      let errorCount = 0;

      while (pickedFiles.length) {
        const localFile = pickedFiles.shift();

        try {
          const id = await insertLocalImage(localFile, { position: 'same-as-gallery', from: 'canvas' });

          if (uploadedCount === 0) {
            viewportActions.locateElement(id, { animate: true, keepScale: true });
          }

          uploadedCount += 1;
        } catch (error) {
          errorCount += 1;
        }
      }

      if (!uploadedCount) {
        if (pickedFiles.length > 1) {
          throw new Error(translate('upload_all_fail_toast'));
        } else {
          throw new Error(translate('upload_fail_toast'));
        }
      }

      // TODO
      // upload and insert elements

      if (!errorCount) {
        notifySuccess(translate('upload_success_toast'));
      } else {
        notifySuccess(translate('upload_completed_toast', { errorCount }));
      }
      nextGuidance();
      mc('landing_upload_image');
    } catch (error) {
      notifyError(translate('upload_fail_toast'), error.message || error.msg || error);
    } finally {
      setImageUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useHotkeys(
    [
      ['v', () => handleActiveTool({ name: 'select' })],
      ['escape', () => handleActiveTool({ name: 'select' })],
      ['h', () => handleActiveTool({ name: 'move' })],
      ['n', handlePickImage],
    ],
    ['INPUT', 'TEXTAREA'],
  );

  const [canvasToolSelectedIdx, setCanvasToolSelectedIdx] = useState(0);
  const handleSelectChange = (index) => {
    setCanvasToolSelectedIdx(index);
    setTempState({ currentCanvasTool: CanvasToolSelectList[index]?.name || 'select' });
  };

  const textTool = extensionTools?.find((item) => item.name === 'text');
  const shapeTools = extensionTools?.filter((item) => /(shape_circle)|(shape_square)|(lines)/.test(item.name));
  const restTools = extensionTools?.filter(
    (item) => !shapeTools?.some((_t) => _t.name === item.name) && textTool?.name !== item.name,
  );
  const [selectedShapeToolIdx, setSelectedShapeToolIdx] = useState(0);
  const handleShapeChange = (index) => {
    setSelectedShapeToolIdx(index);
    setTempState({ currentCanvasTool: shapeTools[index]?.name || shapeTools[0]?.name || 'shape_square' });
  };

  console.log('shapeTools', shapeTools);

  return (
    <div className="h-full flex items-center overflow-hidden shadow-light">
      <StarIconPopOver
        className="ml-5"
        iconStyle="icon"
        activeName={currentCanvasTool}
        selectedIdx={canvasToolSelectedIdx}
        dropdownList={CanvasToolSelectList}
        onChange={handleSelectChange}
      />

      <Tooltip
        withArrow
        position="bottom"
        openDelay={500}
        offset={10}
        className="scale-90"
        label={translate('tool_upload')}
      >
        <div
          className={`flex flex-col items-center justify-center w-[38px] h-full text-[#D3D3D3] disabled:text-primary mr-[1px] ${
            guidance9Visible ? 'z-[71]' : ''
          }`}
          onClick={handlePickImage}
        >
          <NavButton name="image" opened={imageUploading || currentCanvasTool === 'image'}>
            {imageUploading ? (
              <Loader size={16} />
            ) : (
              <Icon className="shk-shangchuantupian" fontSize={16} color="inherit" />
            )}
          </NavButton>
          <GuidePopover visible={guidance9Visible} position="bottom" index={'Import'}></GuidePopover>
        </div>
      </Tooltip>
      {!!textTool && (
        <Tooltip
          withArrow
          key={`${textTool.name}`}
          position="bottom"
          openDelay={500}
          offset={10}
          disabled={!textTool.tooltip}
          className="scale-90"
          label={textTool.tooltip}
        >
          <NavButton
            name={textTool.name}
            iconName={currentCanvasTool === textTool.name ? textTool.activeIcon || textTool.icon : textTool.icon}
            opened={currentCanvasTool === textTool.name}
            onClick={handleClickTool}
          />
        </Tooltip>
      )}
      {shapeTools.length > 0 && (
        <StarIconPopOver
          iconStyle="icon"
          activeName={currentCanvasTool}
          className="mx-[10px]"
          selectedIdx={selectedShapeToolIdx}
          dropdownList={shapeTools}
          onChange={handleShapeChange}
        />
      )}
      {restTools?.map((item, index) => {
        const isActive = currentCanvasTool === item.name;
        const iconName = isActive ? item.activeIcon || item.icon : item.icon;

        return (
          <Tooltip
            withArrow
            key={`${item.name}-${index}`}
            position="bottom"
            openDelay={500}
            offset={10}
            disabled={!item.tooltip}
            className="scale-90"
            label={item.tooltip}
          >
            <NavButton name={item.name} iconName={iconName} opened={isActive} onClick={handleClickTool} />
          </Tooltip>
        );
      })}
      {/* <NavButton
        name="image"
        opened={imageUploading || currentCanvasTool === 'image'}
        onClick={() => setIsShowWorkFlowModal(!isShowWorkFlowModal)}
      >
        <Icon className="shk-fanhui" fontSize={16} color="inherit" />
      </NavButton> */}
    </div>
  );
});
